export interface PageListItem {
  identifier: string;
  title: string | null;
  sharedAt: string;
  advisorName: string | null;
}

export interface PageSection {
  key: string;
  label: string | null;
  items: PageListItem[];
}

// Below this, a flat list reads better than one-row groups.
export const GROUPING_THRESHOLD = 8;

// Sorted defensively so the UI never depends on transport order. Unparseable
// dates sink to the end rather than poisoning the comparator with NaN.
export function sortPagesNewestFirst(items: PageListItem[]): PageListItem[] {
  const time = (item: PageListItem) => {
    const parsed = Date.parse(item.sharedAt);
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  };
  return [...items].sort((a, b) => time(b) - time(a));
}

function startOfDay(date: Date): Date {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
}

function daysBefore(day: Date, count: number): Date {
  const result = new Date(day);
  result.setDate(result.getDate() - count);
  return result;
}

// Full dates, never abbreviated: a client's record of what their advisor sent.
export function formatSharedAt(sharedAt: string): string {
  const parsed = Date.parse(sharedAt);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return new Date(parsed).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Expects newest-first input: sections come out in first-seen order, which is
// then already chronological.
export function groupPagesByRecency(
  items: PageListItem[],
  now = new Date(),
): PageSection[] {
  const today = startOfDay(now);
  const weekStart = daysBefore(today, 6);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const sections = new Map<string, PageSection>();

  for (const item of items) {
    const parsed = Date.parse(item.sharedAt);
    const {key, label} = sectionFor(parsed, {today, weekStart, monthStart});
    const section = sections.get(key) ?? {key, label, items: []};
    section.items.push(item);
    sections.set(key, section);
  }

  return [...sections.values()];
}

function sectionFor(
  timestamp: number,
  bounds: {today: Date; weekStart: Date; monthStart: Date},
): {key: string; label: string} {
  if (!Number.isFinite(timestamp)) {
    return {key: 'earlier', label: 'Earlier'};
  }
  if (timestamp >= bounds.today.getTime()) {
    return {key: 'today', label: 'Today'};
  }
  if (timestamp >= bounds.weekStart.getTime()) {
    return {key: 'week', label: 'This week'};
  }
  if (timestamp >= bounds.monthStart.getTime()) {
    return {key: 'month', label: 'Earlier this month'};
  }
  const date = new Date(timestamp);
  return {
    key: `${date.getFullYear()}-${date.getMonth()}`,
    label: date.toLocaleDateString(undefined, {month: 'long', year: 'numeric'}),
  };
}

// Rows with no snapshot advisor (legacy versions) don't count against a match.
export function singleAdvisorName(items: PageListItem[]): string | null {
  const names = new Set<string>();
  for (const item of items) {
    if (item.advisorName) {
      names.add(item.advisorName);
    }
  }
  if (names.size !== 1) {
    return null;
  }
  const [name] = names;
  return name;
}
